import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { TextField } from "@mui/material";
import S3FileUpload from "@/components/common/inputs/S3FileUpload";
import {
  AcceptedFileTypes,
  SelectedDocument,
} from "@/components/common/inputs/FileUpload";
import { toast } from "react-toastify";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { useNavigate, useParams } from "react-router-dom";
import CloseIcon from "@styles/all-images/icons/closeblack.svg";
import { UserDocumentTabType } from "@/constants/enums/user-document-tab-type.enum";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

interface CreateTermsSheetProps {
  isModal?: boolean;
  onClose?: () => void;
  onSubmit?: () => void;
  isModalOpen?: boolean;
  initialData?: any;
  mode?: "create" | "edit";
  isLoading?: boolean;
}

const CreateTermsSheet: React.FC<CreateTermsSheetProps> = ({
  isModal = false,
  onClose,
  onSubmit,
  isModalOpen,
  initialData,
  mode = "create",
  isLoading = false,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [t3Users, setT3Users] = useState<any[]>([]);
  const [t4Clients, setT4Clients] = useState<any[]>([]);
  const [uploadedFileDetails, setUploadedFileDetails] =
    useState<SelectedDocument | null>(null);
  const [folderId, setFolderId] = useState<number | null>(null);
  const [isUsersLoaded, setIsUsersLoaded] = useState(false);

  const validationSchema = Yup.object({
    t4Client: Yup.string().required("Client Name is required"),
    documentName: Yup.string()
      .required("Document Name is missing")
      .matches(/^[a-zA-Z0-9\s]*$/, "Only alphanumeric characters are allowed")
      .max(100, "Document Name maximum limit of 100 characters"),
    file_name: Yup.string().required("Document is required"),
  });

  const formik = useFormik({
    initialValues: {
      t3User: "",
      t4Client: initialData?.super_trader?.id || "",
      documentName: initialData?.document_title || "",
      file_name: initialData?.file_name || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  const fetchFolderId = useCallback(
    (type: UserDocumentTabType) => {
      return new Promise(async (resolve, reject) => {
        try {
          const { authenticatedCall: fetchFolderId } =
            actionsHandler("createFolder");
          const response = await dispatch(
            fetchFolderId({
              method: "get",
            }),
          );
          if (response.ok) {
            const folderId = response.data.data.find(
              (folder: any) => folder.folder_type === type,
            );
            resolve(folderId.id);
          }
        } catch (error) {
          console.error("Error fetching tabs:", error);
          reject(error);
        }
      });
    },
    [dispatch],
  );

  const handleSettingFolderId = useCallback(async () => {
    const folderId = await fetchFolderId(UserDocumentTabType.termsSheet);
    setFolderId(folderId as number);
  }, [fetchFolderId]);

  const fetchUsersByTier = async () => {
    try {
      // Fetch T3 users (tier_id: 3)
      if (!isModal) {
        const { authenticatedCall: getT3Users } = actionsHandler("userList");
        const t3Response = await dispatch(
          getT3Users({
            method: "get",
            customParams: [`${UserTierType.Tier3}/`],
          }),
        );
        if (t3Response?.ok) setT3Users(t3Response.data);
      }

      // Fetch T4 users (tier_id: 4)
      const { authenticatedCall: getT4Users } = actionsHandler("userList");
      const t4Response = await dispatch(
        getT4Users({
          method: "get",
          customParams: [`${UserTierType.Tier4}/`],
        }),
      );

      if (t4Response?.ok) {
        setT4Clients(t4Response.data);
        setIsUsersLoaded(true);

        // If in edit mode and selections are disabled, set loading to false
        if (mode === "edit" && initialData) {
          setIsLoadingData(false);
        }
      }

      // Then fetch document details
      if (!isModal) {
        const { authenticatedCall: getDocDetails } =
          actionsHandler("imDocumentListById");
        const response = await dispatch(
          getDocDetails({
            method: "get",
            customParams: [id + "/"],
          }),
        );

        if (response?.ok) {
          const { super_trader, created_by } = response.data.data;

          formik.setValues({
            ...formik.values,
            t3User: created_by.id,
            t4Client: super_trader.id,
          });
          console.log({ ...response.data.data.super_trader }.id);
          // Set loading to false after setting values
          setIsLoadingData(false);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (initialData && mode === "edit") {
      let selectedDocument = {
        file_name: initialData.file_name,
        s3_key: initialData.s3_key,
        version_id: initialData.version_id,
        size: initialData.size,
        folder_id: folderId,
        id: initialData.id,
        status: initialData.status,
      };
      setUploadedFileDetails(selectedDocument as any);
      formik.setValues({
        t3User: "",
        t4Client: initialData?.super_trader?.id || "",
        documentName: initialData?.document_title || "",
        file_name: initialData?.file_name || "",
      });
      // Set loading to false after data is set
      setIsLoadingData(false);
    }
  }, [initialData, mode, folderId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingData(true);
        await Promise.all([handleSettingFolderId(), fetchUsersByTier()]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsLoadingData(false);
      }
    };

    // Always fetch initial data, regardless of modal state
    fetchInitialData();
  }, []); // Empty dependency array since we want this to run once on mount

  const handleFileUploadSuccess = (payload: SelectedDocument) => {
    setUploadedFileDetails(payload);
    formik.setFieldValue("file_name", payload.file_name);
  };

  const handleSubmitAction = async (isSend: boolean) => {
    try {
      // Touch all fields to trigger validation
      Object.keys(formik.values).forEach((key) => {
        formik.setFieldTouched(key, true);
      });

      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        return;
      }

      setIsLoadingData(true);

      const { authenticatedCall } = actionsHandler(
        mode === "edit"
          ? "updateTsDocument"
          : !isModal
            ? "createTsDocument"
            : "addTsDocument",
      );

      const response = await dispatch(
        authenticatedCall({
          method: mode === "edit" ? "put" : "post",
          ...(mode === "edit" && { customParams: [initialData.id + "/"] }),
          payload: {
            ...(!isModal && { im_document_id: id }),
            user_id: isModal ? formik.values.t4Client : formik.values.t3User,
            ...(!isModal && { client_id: formik.values.t4Client }),
            document_title: formik.values.documentName,
            file_name:
              uploadedFileDetails?.file_name || formik.values.file_name,
            s3_key: uploadedFileDetails?.s3_key,
            version_id: uploadedFileDetails?.version_id,
            size: uploadedFileDetails?.size,
            is_send: isSend,
            folder_id: folderId,
          },
        }),
      );

      if (response.ok) {
        toast.success(
          response.data.message ||
            `Terms Sheet ${isSend ? "sent" : "added"} successfully`,
        );
        if (!isModal) {
          navigate("/document/im-document");
        }
        formik.resetForm(); // Reset form values
        setUploadedFileDetails(null); // Reset file upload
        onSubmit?.();
        onClose?.();
      }
    } catch (error) {
      console.error(`Failed to ${isSend ? "send" : "add"} Terms Sheet:`, error);
      toast.error(`Failed to ${isSend ? "send" : "add"} Terms Sheet`);
    } finally {
      setIsLoadingData(false);
    }
  };

  const renderFormContent = () => (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        formik.handleSubmit(e);
      }}
      className={isModal ? "" : "tw-mx-6"}
    >
      <div
        className={`${isModal ? "" : "tw-px-6"} tw-w-full tw-bg-white ${!isModal && "tw-border tw-py-4 tw-rounded-lg tw-border-[#E9EAEB]"}`}
      >
        <div
          className={`tw-grid ${isModal ? "tw-grid-cols-1" : "tw-grid-cols-3"} tw-gap-6 tw-mb-6`}
        >
          {/* T3 User Select - Only show for T1/T2 users */}
          {!isModal && (
            <div className="form-group">
              <DynamicAutocomplete
                name="t3User"
                label="Associated User Name (T3)"
                options={t3Users}
                selectedValue={Number(formik.values.t3User)}
                labelKey="name"
                valueKey="id"
                onChange={(value: any) => {
                  formik.setFieldValue("t3User", value);
                }}
                disabled
              />
              {formik.touched.t3User && formik.errors.t3User && (
                <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                  {String(formik.errors.t3User)}
                </div>
              )}
            </div>
          )}

          {/* Client Name (T4) */}

          <div className="form-group">
            <DynamicAutocomplete
              name="t4Client"
              label="Client Name (T4)"
              options={t4Clients}
              selectedValue={Number(formik.values.t4Client)}
              labelKey="name"
              valueKey="id"
              onChange={(value: any) => {
                formik.setFieldValue("t4Client", String(value));
              }}
              touched={Boolean(formik.touched.t4Client)}
              error={String(formik.errors.t4Client)}
              disabled={!isModal ? true : false}
            />
          </div>

          {/* Document Name */}
          <div className="form-group">
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
              Document Name
            </label>
            <TextField
              fullWidth
              name="documentName"
              value={formik.values.documentName}
              onChange={formik.handleChange}
              error={
                formik.touched.documentName &&
                Boolean(formik.errors.documentName)
              }
              placeholder="Enter document name"
              className="tw-h-11"
            />
            {formik.touched.documentName && formik.errors.documentName && (
              <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                {String(formik.errors.documentName)}
              </div>
            )}
          </div>

          {/* File Upload */}
          <div className={`${isModal ? "tw-mt-0" : "tw-mt-6"}`}>
            {folderId && (
              <S3FileUpload
                label="Upload Terms Sheet Document"
                folderId={folderId}
                accept={[AcceptedFileTypes.PDF]}
                disableIsNA={true}
                maxFileSize={250 * 1000 * 1000}
                id="fileInput-1"
                onSuccessUploadCallback={handleFileUploadSuccess}
                selectedDocument={uploadedFileDetails || {}}
              />
            )}
            {formik.touched.file_name && formik.errors.file_name && (
              <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                {String(formik.errors.file_name)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Regular submit button - Only show in non-modal */}
      {!isModal && (
        <div className="tw-flex tw-justify-center tw-gap-4 tw-mt-8">
          <button
            type="button"
            onClick={() => handleSubmitAction(false)}
            disabled={isLoadingData}
            className="tw-bg-[#146C94] tw-text-white tw-px-6 tw-py-2 tw-rounded-md disabled:tw-opacity-50 hover:tw-bg-[#0D5272] tw-transition-colors"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => handleSubmitAction(true)}
            disabled={isLoadingData}
            className="tw-bg-[#146C94] tw-text-white tw-px-6 tw-py-2 tw-rounded-md disabled:tw-opacity-50 hover:tw-bg-[#0D5272] tw-transition-colors"
          >
            Send
          </button>
        </div>
      )}
    </form>
  );

  const renderModalContent = () => {
    if (isLoadingData) {
      return (
        <div className="tw-flex tw-justify-center tw-items-center tw-h-[400px]">
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-[#146C94]" />
        </div>
      );
    }
    return renderFormContent();
  };

  if (isModal) {
    return (
      <div className="tw-fixed tw-inset-0 tw-bg-black/25 tw-flex tw-items-center tw-justify-end tw-z-50">
        <div className="tw-bg-white tw-shadow-lg tw-h-screen tw-w-[350px] tw-flex tw-flex-col">
          {/* Sticky Header */}
          <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-4 tw-border-b tw-sticky tw-top-0 tw-bg-white tw-z-10">
            <h2 className="tw-text-base tw-font-semibold">
              {mode === "edit" ? "Edit" : "Create"} Terms Sheet
            </h2>
            <button onClick={onClose}>
              <img
                src={CloseIcon}
                alt="Close"
                className="tw-w-auto tw-h-auto"
              />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="tw-flex-1 tw-overflow-y-auto">
            <div className="tw-px-4 tw-py-4">{renderModalContent()}</div>
          </div>

          {/* Sticky Footer */}
          <div className="tw-px-4 tw-py-4 tw-border-t tw-sticky tw-bottom-0 tw-bg-white tw-z-10">
            <div className="tw-space-x-4 tw-flex">
              <button
                type="button"
                onClick={() => handleSubmitAction(false)}
                disabled={isLoadingData}
                className="tw-flex-1 tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md disabled:tw-opacity-50"
              >
                {mode === "edit" ? "Update" : "Add"}
              </button>
              <button
                type="button"
                onClick={() => handleSubmitAction(true)}
                disabled={isLoadingData}
                className="tw-flex-1 tw-h-11 tw-bg-[#146C94] tw-text-white tw-px-4 tw-rounded-md disabled:tw-opacity-50"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render page version for T1/T2 users
  if (!isModal) {
    return (
      <div className="tw-relative">
        <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
          <div className="tw-px-6 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
            <div className="tw-flex tw-justify-between tw-items-center">
              <div className="tw-flex tw-items-center">
                <img
                  src={BackIcon}
                  alt="Back"
                  onClick={() => navigate(-1)}
                  className="tw-cursor-pointer"
                />
                <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                  Create Terms Sheet
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="tw-mt-[167px] tw-w-full">
          {isLoading || !folderId || !isUsersLoaded ? (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-[calc(100vh-250px)]">
              <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-[#146C94]" />
            </div>
          ) : (
            renderFormContent()
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-6 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <img
                src={BackIcon}
                alt="Back"
                onClick={() => navigate(-1)}
                className="tw-cursor-pointer"
              />
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Create Terms Sheet
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-mt-[167px] tw-w-full">
        {isLoading || !folderId ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-[calc(100vh-250px)]">
            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-[#146C94]" />
          </div>
        ) : (
          renderFormContent()
        )}
      </div>
    </div>
  );
};

export default CreateTermsSheet;
